// PerformanceExportUtils.js - Updated with School Fees support

import { pdfApi, excelApi } from './api';

/**
 * Utility class to handle PDF/Excel export operations for Performance Analysis
 */
export class PerformanceExportUtils {
  constructor(groupId, groupName) {
    this.groupId = groupId;
    this.groupName = groupName;
  }

  /**
   * Generate and download PDF for the current period
   * @param {Object} currentPeriod - The current period data
   * @param {boolean} isSchoolFees - Whether this is for school fees account
   * @param {Function} onStart - Callback when operation starts
   * @param {Function} onSuccess - Callback when operation succeeds
   * @param {Function} onError - Callback when operation fails
   */
  async exportToPDF(currentPeriod, isSchoolFees = false, onStart, onSuccess, onError) {
    try {
      onStart && onStart();
  
      // Prepare the request options based on current period
      const options = {
        isSchoolFees: isSchoolFees
      };
      
      if (currentPeriod) {
        options.periodStart = currentPeriod.period_start;
        options.periodEnd = currentPeriod.period_end;
      }
  
      console.log('📄 Generating PDF with options:', options);
  
      // Generate and download PDF - backend returns {blob, filename}
      const result = await pdfApi.generateAndDownloadPDF(this.groupId, options);
      
      // Use the actual filename from backend
      const filename = result.filename || this._generateFallbackFilename(currentPeriod, isSchoolFees, 'pdf');
      
      // Trigger download
      pdfApi.triggerDownload(result.blob, filename);
      
      onSuccess && onSuccess(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      onError && onError(error.message || 'Failed to generate PDF');
    }
  }

  /**
   * Generate and download Excel for the current period
   * @param {Object} currentPeriod - The current period data
   * @param {boolean} isSchoolFees - Whether this is for school fees account
   * @param {Function} onStart - Callback when operation starts
   * @param {Function} onSuccess - Callback when operation succeeds
   * @param {Function} onError - Callback when operation fails
   */
  async exportToExcel(currentPeriod, isSchoolFees = false, onStart, onSuccess, onError) {
    try {
      onStart && onStart();

      const options = {
        isSchoolFees: isSchoolFees  // ✅ ADD THIS
      };
      
      if (currentPeriod) {
        options.periodStart = currentPeriod.period_start;
        options.periodEnd = currentPeriod.period_end;
      }

      console.log('Generating Excel with options:', options);

      // Generate and download Excel
      const result = await excelApi.generateAndDownloadExcel(this.groupId, options);
      
      // Use the actual filename from backend
      const filename = result.filename || this._generateFallbackFilename(currentPeriod, isSchoolFees, 'xlsx');
      
      // Trigger download
      excelApi.triggerDownload(result.blob, filename);
      
      onSuccess && onSuccess(filename);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      onError && onError(error.message || 'Failed to generate Excel');
    }
  }

  /**
   * Generate fallback filename if backend doesn't provide one
   * @private
   */
  _generateFallbackFilename(currentPeriod, isSchoolFees, extension) {
    const periodName = currentPeriod?.period_name || 'current';
    const accountType = isSchoolFees ? 'SchoolFees' : 'Regular';
    const sanitizedGroupName = this.groupName.replace(/[^a-zA-Z0-9]/g, '_');
    const sanitizedPeriodName = periodName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${sanitizedGroupName}_${accountType}_performance_${sanitizedPeriodName}.${extension}`;
  }

  /**
   * Generate PDF without immediate download (for getting download link)
   * @param {Object} currentPeriod - The current period data
   * @param {boolean} isSchoolFees - Whether this is for school fees account
   * @returns {Promise<Object>} PDF generation response with download link
   */
  async generatePDFInfo(currentPeriod, isSchoolFees = false) {
    const options = {
      isSchoolFees: isSchoolFees,  // ✅ ADD THIS
      download: false // Don't download immediately
    };
    
    if (currentPeriod) {
      options.periodStart = currentPeriod.period_start;
      options.periodEnd = currentPeriod.period_end;
    }

    return await pdfApi.generateGroupPDF(this.groupId, options);
  }

  /**
   * List all available PDFs for this group
   * @returns {Promise<Object>} List of available PDFs
   */
  async listAvailablePDFs() {
    return await pdfApi.listGroupPDFs(this.groupId);
  }

  /**
   * Download a specific PDF file by filename
   * @param {string} filename - The filename to download
   */
  async downloadSpecificPDF(filename) {
    try {
      const pdfBlob = await pdfApi.downloadPDF(this.groupId, filename);
      pdfApi.triggerDownload(pdfBlob, filename);
    } catch (error) {
      console.error('Error downloading specific PDF:', error);
      throw error;
    }
  }

  /**
   * Delete a specific PDF file
   * @param {string} filename - The filename to delete
   * @returns {Promise<Object>} Delete response
   */
  async deletePDF(filename) {
    return await pdfApi.deletePDF(this.groupId, filename);
  }

  /**
   * Share performance analysis by generating PDF and returning download link
   * @param {Object} currentPeriod - The current period data
   * @param {boolean} isSchoolFees - Whether this is for school fees account
   * @returns {Promise<Object>} Sharing information with download link
   */
  async sharePerformanceAnalysis(currentPeriod, isSchoolFees = false) {
    try {
      // Generate PDF and get download link from backend
      const pdfInfo = await this.generatePDFInfo(currentPeriod, isSchoolFees);
      
      const accountType = isSchoolFees ? 'School Fees' : 'Regular';
      
      // Return sharing information
      return {
        success: true,
        downloadUrl: pdfInfo.download_url,
        message: `${accountType} PDF generated successfully. You can share the download link.`,
        filename: pdfInfo.filename,
        accountType: accountType
      };
      
    } catch (error) {
      console.error('Error preparing share:', error);
      throw error;
    }
  }

  /**
   * ✅ NEW: Export based on format selection (unified method)
   * @param {string} format - 'pdf' or 'excel'
   * @param {Object} currentPeriod - The current period data
   * @param {boolean} isSchoolFees - Whether this is for school fees account
   * @param {Function} onStart - Callback when operation starts
   * @param {Function} onSuccess - Callback when operation succeeds
   * @param {Function} onError - Callback when operation fails
   */
  async export(format, currentPeriod, isSchoolFees = false, onStart, onSuccess, onError) {
    if (format === 'pdf') {
      return this.exportToPDF(currentPeriod, isSchoolFees, onStart, onSuccess, onError);
    } else if (format === 'excel') {
      return this.exportToExcel(currentPeriod, isSchoolFees, onStart, onSuccess, onError);
    } else {
      const error = `Unsupported format: ${format}`;
      onError && onError(error);
      throw new Error(error);
    }
  }
}

/**
 * Factory function to create PerformanceExportUtils instance
 * @param {string|number} groupId - Group ID
 * @param {string} groupName - Group name
 * @returns {PerformanceExportUtils} Export utils instance
 */
export const createPerformanceExportUtils = (groupId, groupName) => {
  return new PerformanceExportUtils(groupId, groupName);
};

export default PerformanceExportUtils;